<template>
  <b-modal
    v-model="visible"
    centered
    scrollable
    hide-header-close
    hide-footer
    :no-close-on-backdrop="entranceLocked"
    :content-class="reducedMotion ? '' : 'animate__animated animate__zoomIn'"
    :title="$t('freeAccess.celebration.title')"
    @shown="onShown"
    @hidden="onHidden"
  >
    <div class="text-center p-2">
      <feather-icon
        icon="GiftIcon"
        size="48"
        class="text-primary mb-2"
      />
      <h3>{{ $t(headlineKey) }}</h3>
      <p>{{ $t(bodyKey, { date: formattedEnd }) }}</p>
      <div
        v-if="approvalNote"
        class="bg-light-primary rounded text-left p-1 mb-2"
        data-testid="celebration-approval-note"
      >
        <p class="font-weight-bold mb-50">
          {{ $t('freeAccess.celebration.approvalNoteLabel') }}
        </p>
        <p
          class="celebration-approval-note__text mb-0"
          v-text="approvalNote"
        />
      </div>
      <b-button
        variant="primary"
        :to="{ name: 'home' }"
        @click="visible = false"
      >
        {{ $t('freeAccess.celebration.cta') }}
      </b-button>
      <b-button
        variant="link"
        data-testid="close-free-access-celebration"
        @click="visible = false"
      >
        {{ $t('freeAccess.celebration.close') }}
      </b-button>
    </div>
  </b-modal>
</template>
<script>
import { BModal, BButton } from 'bootstrap-vue'
import 'animate.css'
import { formatApprovalLifecycleDate } from '@/libs/approvalLifecycle'
import {
  isFreeAccessActive, freeAccessCelebrationKey, hasSeenFreeAccessCelebration, markFreeAccessCelebrationSeen,
} from '@/libs/freeAccess'

const openKeys = new Set()
export default {
  components: { BModal, BButton },
  props: {
    freeAccess: { type: Object, required: true },
    userId: { type: String, required: true },
    approval: { type: Object, default: null },
  },
  data() {
    return {
      visible: false, entranceLocked: true, reducedMotion: false, timers: [], reservedKey: null, destroyed: false,
    }
  },
  computed: {
    celebrationKey() { return freeAccessCelebrationKey(this.userId, this.freeAccess.startedAt) },
    formattedEnd() { return formatApprovalLifecycleDate(this.freeAccess.endsAt) },
    headlineKey() { return this.freeAccess.source === 'admin-grant' ? 'freeAccess.celebration.adminHeadline' : 'freeAccess.celebration.headline' },
    bodyKey() {
      return { approval: 'freeAccess.celebration.approvalBody', 'admin-grant': 'freeAccess.celebration.adminBody' }[this.freeAccess.source] || 'freeAccess.celebration.body'
    },
    approvalNote() {
      if (!['approval', 'self-claim'].includes(this.freeAccess.source)
        || !this.approval || this.approval.status !== 'approved') return ''
      return typeof this.approval.reviewComment === 'string' ? this.approval.reviewComment.trim() : ''
    },
  },
  watch: {
    celebrationKey: { immediate: true, handler() { this.maybeOpen() } },
    freeAccess: { deep: true, handler() { this.maybeOpen() } },
  },
  beforeDestroy() {
    this.destroyed = true
    this.timers.forEach(clearTimeout)
    if (this.reservedKey) openKeys.delete(this.reservedKey)
  },
  methods: {
    maybeOpen() {
      if (!isFreeAccessActive(this.freeAccess)) { this.visible = false; return }
      if (!this.userId || !this.freeAccess.startedAt || this.visible || openKeys.has(this.celebrationKey)
        || hasSeenFreeAccessCelebration(this.celebrationKey)) return
      this.reducedMotion = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      this.reservedKey = this.celebrationKey
      openKeys.add(this.reservedKey)
      this.visible = true
    },
    async onShown() {
      markFreeAccessCelebrationSeen(this.celebrationKey)
      this.timers.push(setTimeout(() => { this.entranceLocked = false }, 1500))
      if (this.reducedMotion) return
      try {
        const loaded = await import('canvas-confetti')
        if (this.destroyed || !this.visible) return
        const confetti = loaded.default || loaded;
        [0, 600, 1200].forEach(delay => {
          this.timers.push(setTimeout(() => {
            if (this.visible) {
              confetti({
                particleCount: 60, spread: 75, origin: { y: 0.65 }, disableForReducedMotion: true,
              })
            }
          }, delay))
        })
      } catch (_) { /* The modal works without the optional animation. */ }
    },
    onHidden() {
      if (this.reservedKey) openKeys.delete(this.reservedKey)
      this.$emit('closed')
    },
  },
}
</script>

<style scoped>
.celebration-approval-note__text {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
</style>
